import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, Save, Power, Clock } from "lucide-react";

export default function SuperAdminMaintenanceMode() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/super-admin/maintenance");
            setCfg(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    };
    useEffect(() => { load(); }, []);

    const save = async (patch) => {
        setBusy(true);
        try {
            const r = await api.put("/super-admin/maintenance", patch);
            setCfg(r.data);
            toast.success("Maintenance settings updated");
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setBusy(false); }
    };

    if (!cfg) return <AdminShell><div className="text-[#17212B]/60">Loading…</div></AdminShell>;

    const etaLocal = cfg.eta_iso ? new Date(cfg.eta_iso).toISOString().slice(0, 16) : "";

    return (
        <AdminShell>
            <div className="mb-4">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin · Maintenance mode</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Platform maintenance toggle</h1>
                <p className="text-[#17212B]/60 mt-1 text-sm">
                    When ON, marketing pages + doctor login/dashboards return 503 with <code>Retry-After</code>. Super admin portal is <b>always</b> accessible. Published <code>/dr/&lt;slug&gt;</code> pages stay LIVE by default.
                </p>
            </div>

            <Card className={cfg.enabled ? "border-red-300 bg-red-50" : ""} data-testid="maintenance-toggle-card">
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Power className={cfg.enabled ? "text-red-600" : "text-emerald-600"} size={18} />
                        Maintenance is currently <span className={cfg.enabled ? "text-red-700" : "text-emerald-700"}>{cfg.enabled ? "ON" : "OFF"}</span>
                    </CardTitle>
                    <Switch
                        checked={!!cfg.enabled}
                        onCheckedChange={(v) => save({ enabled: v })}
                        data-testid="maintenance-enabled-switch"
                    />
                </CardHeader>
                <CardContent className="space-y-4">
                    {cfg.enabled && (
                        <div className="text-xs text-red-700 bg-white border border-red-200 rounded px-3 py-2 flex items-start gap-2" data-testid="maintenance-on-banner">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                            <div>
                                Every non-super-admin request to <code>/api/*</code> (except auth login/logout) is being 503'd right now. Turn OFF before end of your maintenance window.
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs">Message shown to users</Label>
                        <Textarea rows={3} value={cfg.message || ""}
                            onChange={(e) => setCfg({ ...cfg, message: e.target.value })}
                            data-testid="maintenance-message" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs flex items-center gap-1"><Clock size={12} />Estimated back-online time</Label>
                            <Input type="datetime-local" value={etaLocal}
                                onChange={(e) => setCfg({ ...cfg, eta_iso: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                data-testid="maintenance-eta" />
                            <div className="text-[10px] text-[#17212B]/50 mt-1">Powers the countdown on the "We'll be back" page + Retry-After header.</div>
                        </div>
                        <div className="flex items-start gap-3 pt-4">
                            <Switch
                                checked={!!cfg.include_doctor_public_pages}
                                onCheckedChange={(v) => save({ include_doctor_public_pages: v })}
                                data-testid="maintenance-include-doctors"
                            />
                            <div>
                                <div className="text-xs font-semibold">Also block published /dr/&lt;slug&gt; pages</div>
                                <div className="text-[11px] text-[#17212B]/60 mt-0.5">
                                    OFF (default) keeps every doctor's public page live — recommended. Turn ON only for infrastructure work that would corrupt renders.
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button variant="outline" onClick={load}>Reset</Button>
                        <Button onClick={() => save({ message: cfg.message, eta_iso: cfg.eta_iso })}
                            disabled={busy} data-testid="maintenance-save-copy">
                            <Save size={14} /> {busy ? "Saving…" : "Save message + ETA"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardHeader><CardTitle className="text-base">Preview</CardTitle></CardHeader>
                <CardContent>
                    <div className="rounded-md bg-slate-900 text-white p-6 text-center">
                        <div className="text-xs uppercase tracking-widest text-[#5EEAD4]">Scheduled maintenance</div>
                        <div className="mt-2 text-2xl font-bold">We'll be back very soon.</div>
                        <div className="mt-3 text-white/70 text-sm">{cfg.message}</div>
                        {cfg.eta_iso && (
                            <div className="mt-4 inline-block bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs">
                                ETA: <b>{new Date(cfg.eta_iso).toLocaleString()}</b>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <div className="mt-4 text-[11px] text-[#17212B]/50">
                Last updated {cfg.updated_at ? `by ${cfg.updated_by_email} · ${new Date(cfg.updated_at).toLocaleString()}` : "—"}.
            </div>
        </AdminShell>
    );
}
